import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y } from 'swiper/modules';
import type SwiperCore from 'swiper';

import 'swiper/css';
import 'swiper/css/navigation'; // If using default Swiper nav, but we'll use custom

import { IProps } from './PullQuoteBlock.interface';
import './PullQuoteBlock.styles.css';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
    ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Text from '@/elements/Text/Text.component';
import Image from '@/elements/Image/Image.component';
import Button from '@/elements/Button/Button.component';
import Icon from '@/elements/Icon/Icon.component'; // Assuming you have an Icon component
import { WysiwygBlock } from '../WysiwygBlock/WysiwygBlock.component';

const defaultQuoteIcon = (
    <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* for white background*/}
        <rect
            x="2.4"
            y="2.46154"
            width="43.2"
            height="34.4615"
            fill="white"
            rx="1"
        />
        <path
        d="M45.6 0C46.9255 0 48 1.10208 48 2.46154V36.9231C48 38.2826 46.9255 39.3846 45.6 39.3846H10.692L0 48V2.46154C0 1.10208 1.07453 0 2.4 0H45.6ZM20.4367 10.8593C16.1478 12.7676 13.2 16.6288 13.2 20.9359C13.2 23.3846 13.8638 24.8953 15.1001 26.2412C15.8784 27.0887 17.1097 27.6923 18.3974 27.6923C20.717 27.6923 22.5974 25.7637 22.5974 23.3846C22.5974 21.1188 20.8908 19.2958 18.7257 19.09C18.3386 19.053 17.9478 19.0599 17.5756 19.1126L17.5763 18.8871C17.589 17.8023 17.8135 14.605 21.5078 12.5548L20.4367 10.8593ZM32.4367 10.8593C28.1479 12.7676 25.2 16.6288 25.2 20.9359C25.2 23.3846 25.8638 24.8953 27.1001 26.2412C27.8784 27.0887 29.1098 27.6923 30.3974 27.6923C32.717 27.6923 34.5974 25.7637 34.5974 23.3846C34.5974 21.1188 32.8908 19.2958 30.7258 19.09C30.3386 19.053 29.9479 19.0599 29.5757 19.1126L29.5762 18.8871C29.5889 17.8023 29.8135 14.605 33.5078 12.5548L32.4367 10.8593Z"
        className='fill-brand-1'
        />
    </svg>
);

const PullQuoteBlock: React.FC<IProps> = ({
    items,
    icon,
    className,
    ...blockWrapperProps
}) => {
    const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const handlePrev = () => {
        if (swiperInstance) {
            swiperInstance.slidePrev();
        }
    };

    const handleNext = () => {
        if (swiperInstance) {
            swiperInstance.slideNext();
        }
    };

    useEffect(() => {
        if (swiperInstance) {
            const updateIndex = () => setActiveIndex(swiperInstance.realIndex);
            swiperInstance.on('slideChange', updateIndex);
            // Initial update
            updateIndex();
            return () => {
                swiperInstance.off('slideChange', updateIndex);
            };
        }
    }, [swiperInstance]);

    if (!items || items.length === 0) {
        return null; // Or some fallback UI
    }

    return (
        <BlockWrapper
            className={clsx('pullQuoteBlock', className)}
            {...blockWrapperProps}
        >
            <ContentWrapper className="!flex-col !gap-0">
                <div className="pullQuoteBlock__swiperContainer">
                    <Swiper
                        modules={[Navigation, A11y]}
                        spaceBetween={50}
                        slidesPerView={1}
                        loop={items.length > 1}
                        onSwiper={setSwiperInstance}
                        className="pullQuoteBlock__swiper"
                    >
                        {items.map((item, index) => (
                            <SwiperSlide key={index} className="pullQuoteBlock__slide">
                                <ContentWrapper>
                                    <ContentColumn>
                                        <div className="pullQuoteBlock__slideContent">
                                            <div className="pullQuoteBlock__iconContainer">
                                                {icon || defaultQuoteIcon}
                                            </div>
                                            {item.quote && (
                                                <WysiwygBlock
                                                    className="pullQuoteBlock__quote"
                                                    text={item.quote}
                                                />
                                            )}
                                            <div className="pullQuoteBlock__footer">
                                                {(item.author || item.job) && (
                                                    <div className="pullQuoteBlock__authorInfo">
                                                        {item.image && (
                                                            <div className="pullQuoteBlock__authorImageContainer">
                                                                <Image
                                                                    {...item.image}
                                                                    className="pullQuoteBlock__authorImage"
                                                                    rounded={true}
                                                                    // Ensure defaultSrc and srcSet are handled by IImageProps
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="pullQuoteBlock__authorDetails">
                                                            {item.author && (
                                                                <Text
                                                                    className="pullQuoteBlock__authorName"
                                                                    html={item.author}
                                                                />
                                                            )}

                                                            {item.job && (
                                                                <Text
                                                                    className="pullQuoteBlock__authorJob"
                                                                    html={item.job}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {items.length > 1 && (
                                                    <div className="pullQuoteBlock__navigation">
                                                        <Button
                                                            variant="primary" // Assuming a variant for nav buttons
                                                            color='brand-1'
                                                            onClick={handlePrev}
                                                            disabled={activeIndex === 0 && !swiperInstance?.params.loop}
                                                            aria-label="Previous quote"
                                                            className="pullQuoteBlock__navButton pullQuoteBlock__navButton--prev w-8 h-8"
                                                            size='xs'
                                                        >
                                                            <Icon icon="chevron-left" className="w-6 h-6" />
                                                        </Button>
                                                        <Button
                                                            variant="primary"
                                                            color='brand-1'
                                                            onClick={handleNext}
                                                            disabled={
                                                                activeIndex === items.length - 1 && !swiperInstance?.params.loop
                                                            }
                                                            aria-label="Next quote"
                                                            className="pullQuoteBlock__navButton pullQuoteBlock__navButton--next w-8 h-8"
                                                            size='xs'
                                                        >
                                                            <Icon icon="chevron-right" className="w-6 h-6" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </ContentColumn>
                                </ContentWrapper>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                
            </ContentWrapper>
        </BlockWrapper>
    );
};

export { PullQuoteBlock };
